// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { getFulfillmentURLLinks } from '@/fulfillment/api/get-fulfillment-url-links';
import { fulfillmentURLLinksQueryKey } from '@/fulfillment/query-keys';
import { useQuery } from '@tanstack/react-query';

interface UseFulfillmentURLLinksOptions {
  /** The key of the fulfillment order to fetch URL links for */
  fulfillmentKey: string;
}

/**
 * Hook to fetch URL links associated with a fulfillment order.
 * Uses a regular query (not suspense) to allow graceful error handling
 * without crashing the entire page.
 *
 * @param options - Options including the fulfillment key
 * @returns The URL links, loading state, error state, and query methods
 */
export function useFulfillmentURLLinks(options: UseFulfillmentURLLinksOptions) {
  const { data, isLoading, isError, error, refetch, ...query } = useQuery({
    queryKey: fulfillmentURLLinksQueryKey(options.fulfillmentKey),
    queryFn: () => getFulfillmentURLLinks(options.fulfillmentKey),
  });

  return {
    urlLinks: data?.data ?? [],
    isLoading,
    isError,
    error,
    refetch,
    ...query,
  };
}
